// DV360-specific transforms that shape a `launcherResult` JSON into the
// objects the shared CLW review grid + summary pane consume. Pure data
// — no React, no I/O. Mirrors the groundtruth/gtRowBuilders.js layout so
// the main component can dispatch by platform without per-platform JSX
// branching.

import {
  ymdFromObj,
  deriveFormat,
  fmtCurrency,
} from '../clwFormatters';
import {
  microsToDollars,
  countryName,
  humanizeTactic,
  lineItemTypeToFormat,
} from './dv360Constants';
import {
  targetingPlanGeo,
  targetingPlanAudienceIds,
  targetingPlanCells,
  targetingPlanEntries,
  planFor,
} from './dv360TargetingPlan';
import {
  parseIoDisplayName,
  getIoImpressions,
  buildPackageIndex,
  sumPackageBudget,
  sumPackageUnits,
  isImpressionBudget,
} from './dv360Parsing';

/**
 * Sum an IO's currency budget across its segments. Imported plans carry the
 * raw DV360 `budget.budgetSegments[].budgetAmountMicros`; launcher plans
 * either match this or state `0` micros for impression-based IOs, so this is
 * a safe zero-value fallback for both.
 */
function ioBudgetMicros(io) {
  // An impressions-unit IO's budgetAmountMicros is an impression COUNT, not
  // money (see isImpressionBudget). Dividing it by 1e6 rendered a 212,717
  // impression goal as a $0.21 budget on every imported plan.
  if (isImpressionBudget(io)) return 0;
  let t = 0;
  for (const seg of io?.budget?.budgetSegments || []) {
    const v = Number(seg?.budgetAmountMicros ?? seg?.budgetAmountInMicros ?? 0);
    if (Number.isFinite(v) && v > 0) t += v;
  }
  return t;
}

// Headline numbers for the summary tiles. Returns null when the input
// is missing so callers can render a placeholder.
export function summarizeDv360Result(result) {
  if (!result) return null;
  const ios = Array.isArray(result.insertionOrders) ? result.insertionOrders : [];
  const lis = Array.isArray(result.lineItems) ? result.lineItems : [];
  const packages = Array.isArray(result.packages) ? result.packages : [];
  // Same per-IO reader buildDv360ReviewRows uses, so the tile and the grid
  // cannot disagree. This was an inlined segments-only sum that duplicated
  // getIoImpressions' old blind spot for imported impressions IOs.
  const { pkgByKey: sumByKey, pkgByIoMatch: sumByMatch } = buildPackageIndex(packages);
  const totalImpressions = sumPackageUnits(packages)
    || ios.reduce((t, io) => t + getIoImpressions(io, sumByKey, sumByMatch), 0);
  // Package-derived budget wins for launcher plans (package CPM × units).
  // Imported plans have no packages — read the raw IO segment micros instead.
  let totalBudget = sumPackageBudget(packages);
  if (!totalBudget) {
    totalBudget = ios.reduce((t, io) => t + ioBudgetMicros(io) / 1e6, 0);
    if (totalBudget > 0) totalBudget = Number(totalBudget.toFixed(2));
  }
  const avgCpm = totalImpressions > 0 && totalBudget > 0 ? (totalBudget / totalImpressions) * 1000 : null;

  const f = result.campaign?.campaignFlight?.plannedDates || {};
  const errors = Array.isArray(result.templateErrors) ? result.templateErrors : [];
  const warnings = Array.isArray(result.templateWarnings) ? result.templateWarnings : [];
  // Count unique audience IDs across all LIs.
  // MMGY-processed plans return this as an object (not an array) on
  // some line items, so the `|| []` fallback isn't enough — guard with
  // Array.isArray to avoid "groups is not iterable".
  const audSet = new Set();
  for (const li of lis) {
    // Tolerates two shapes:
    //  - Canonical DV360: includedFirstPartyAndPartnerAudienceGroups is an ARRAY of groups.
    //  - MMGY-processed: same key is a SINGLE OBJECT with .settings[] directly.
    const groups = li?.audiences?.includedFirstPartyAndPartnerAudienceGroups;
    const audSettings = Array.isArray(groups)
      ? groups.flatMap((g) => (Array.isArray(g?.settings) ? g.settings : []))
      : (groups && Array.isArray(groups.settings) ? groups.settings : []);
    for (const s of audSettings) {
      // Count resolved 1P/partner IDs and non-keyword third-party IDs.
      if (s.firstPartyAndPartnerAudienceId) audSet.add(s.firstPartyAndPartnerAudienceId);
      else if (s.thirdpartyAudienceId && String(s.thirdpartyAudienceId) !== '1000') {
        audSet.add(`tp:${s.thirdpartyAudienceId}`);
      }
    }
  }

  // A COPY states no li.audiences at all — its targeting rides the
  // targeting_plan sidecar (see dv360TargetingPlan). Fill only a genuine
  // absence: a plan-stated audience always wins, so an import is untouched.
  let audienceBasis = null;
  if (audSet.size === 0) {
    const fromPlan = new Set(lis.flatMap((li) => targetingPlanAudienceIds(planFor(result, li.lineItemId))));
    if (fromPlan.size) {
      for (const id of fromPlan) audSet.add(id);
      audienceBasis = 'carried from the source campaign';
    }
  }

  // For impressions-only campaigns (no currency budget), estimate spend
  // from the campaign KPI CPM target × total impressions.
  let estimatedBudget = null;
  if (!totalBudget && totalImpressions > 0) {
    const kpiMicros = Number(result.campaign?.campaignGoal?.performanceGoal?.performanceGoalAmountMicros || 0);
    if (kpiMicros > 0) estimatedBudget = (totalImpressions / 1000) * (kpiMicros / 1e6);
  }

  return {
    platform: 'DV360',
    ioCount: Number(result.insertionOrderCount) || ios.length,
    adGroupCount: Number(result.lineItemCount) || lis.length,
    packageCount: packages.length,
    totalImpressions,
    totalBudget,
    estimatedBudget,
    avgCpm,
    flightStart: ymdFromObj(f.startDate),
    flightEnd: ymdFromObj(f.endDate),
    campaignName: result.campaign?.displayName || null,
    audienceCount: audSet.size,
    audienceBasis,
    issueCount: errors.length + warnings.length,
    errorCount: errors.length,
    warningCount: warnings.length,
    advertiserId: result.advertiserId || null,
  };
}

// One row per IO for the summary-pane "By IO" breakdown table.
export function breakdownDv360ByPackage(result) {
  if (!result) return [];
  const ios = Array.isArray(result.insertionOrders) ? result.insertionOrders : [];
  const packages = Array.isArray(result.packages) ? result.packages : [];
  const { pkgByKey, pkgByIoMatch } = buildPackageIndex(packages);
  const allocableUnits = (io) => {
    // For IOs that share a package, the segments use percentage splits — pull
    // the IO's share by summing its segment percentages × matched package units.
    const direct = io?.packageKey && pkgByKey.get(io.packageKey);
    const meta = parseIoDisplayName(io?.displayName || '');
    const matched = direct
      || (meta.pkId && pkgByIoMatch.get(`pk:${meta.pkId}`))
      || (meta.packageName && pkgByIoMatch.get(`name:${meta.packageName.toLowerCase()}`));
    if (matched?.units) {
      // If multiple IOs share the package, scale by the IO's allocation%.
      // Real plans set per-segment `budgetAmountPercentage`; if all sum to 1
      // for this IO alone, it means this IO gets the whole package.
      const segPct = (io?.budget?.budgetSegments || []).reduce((s, x) => s + Number(x.budgetAmountPercentage || 0), 0);
      if (segPct > 0 && segPct < 1) return Math.round(Number(matched.units) * segPct);
      // Otherwise count siblings that share the same package and split evenly.
      const sibs = ios.filter((other) => {
        const otherMeta = parseIoDisplayName(other.displayName || '');
        return otherMeta.pkId && otherMeta.pkId === meta.pkId;
      }).length || 1;
      return Math.round(Number(matched.units) / sibs);
    }
    return getIoImpressions(io, pkgByKey, pkgByIoMatch);
  };

  return ios.map((io) => {
    const meta = parseIoDisplayName(io.displayName || '');
    const matched = (io.packageKey && pkgByKey.get(io.packageKey))
      || (meta.pkId && pkgByIoMatch.get(`pk:${meta.pkId}`))
      || (meta.packageName && pkgByIoMatch.get(`name:${meta.packageName.toLowerCase()}`));
    const impressions = allocableUnits(io);
    const rate = meta.rate ?? (matched ? Number(matched.rate) : null);
    const budget = rate && impressions ? (rate * impressions) / 1000 : (matched?.budget ?? null);
    const fmtCode = deriveFormat(io.displayName || meta.formatLabel || '', '').toUpperCase();
    const seg0 = io?.budget?.budgetSegments?.[0]?.dateRange || {};
    const segN = io?.budget?.budgetSegments?.[io?.budget?.budgetSegments?.length - 1]?.dateRange || {};
    return {
      key: io.packageKey || meta.pkId || io.displayName,
      formatCode: fmtCode,
      packageName: [meta.packageName, meta.formatLabel].filter(Boolean).join(' · ') || io.displayName,
      categoryLabel: meta.categoryLabel || '—',
      lineItemCount: (io.lineItems || []).length,
      impressions,
      rate,
      budget,
      sizes: matched?.placement_sizes?.join(', ') || '—',
      flightStart: ymdFromObj(seg0.startDate),
      flightEnd: ymdFromObj(segN.endDate),
    };
  });
}

// Per-budget-segment (per month) view straight off the parsed JSON.
export function breakdownDv360ByMonth(result) {
  const ios = Array.isArray(result?.insertionOrders) ? result.insertionOrders : [];
  const packages = Array.isArray(result?.packages) ? result.packages : [];
  const { pkgByKey, pkgByIoMatch } = buildPackageIndex(packages);
  const months = new Map(); // key: budgetPeriodName → row
  for (const io of ios) {
    const ioImps = getIoImpressions(io, pkgByKey, pkgByIoMatch);
    for (const seg of (io.budget?.budgetSegments || [])) {
      const name = seg.budgetPeriodName || `${ymdFromObj(seg.dateRange?.startDate)} → ${ymdFromObj(seg.dateRange?.endDate)}`;
      const pct = Number(seg.budgetAmountPercentage || 0);
      const imps = Math.round(ioImps * pct);
      if (!months.has(name)) months.set(name, { key: name, label: name, impressions: 0, lineItemCount: 0, start: ymdFromObj(seg.dateRange?.startDate), end: ymdFromObj(seg.dateRange?.endDate) });
      months.get(name).impressions += imps;
      months.get(name).lineItemCount += (io.lineItems || []).length;
    }
  }
  return [...months.values()].sort((a, b) => (a.start || '').localeCompare(b.start || ''));
}

// One row per line item for the review grid. Rows are decorated with
// annotations sourced from templateErrors / templateWarnings so the grid
// can light up cells with validation feedback.
/**
 * @param {object} result  the launcherResult
 * @param {object} [opts]
 * @param {boolean} [opts.fillFromTargetingPlan=false]
 *   Fill empty targeting cells from a COPY's `targeting_plan` sidecar, and mark the
 *   row `targetingFromPlan` so the cell-detail panel renders those cells READ-ONLY.
 *
 *   Off by default for two reasons. (1) CopyCampaignWorkflow's buildCopyReviewRows
 *   calls this builder for its base rows and then overlays its OWN derivation of the
 *   same sidecar, replacing only cells it still finds empty — filling them here would
 *   outrank the better value. (2) The read-only marking is what makes the fill SAFE:
 *   see the note on targetingFromPlan below. A caller that opts in must honour it.
 */
export function buildDv360ReviewRows(result, { fillFromTargetingPlan = false } = {}) {
  if (!result) return [];
  const ios = Array.isArray(result.insertionOrders) ? result.insertionOrders : [];
  const lis = Array.isArray(result.lineItems) ? result.lineItems : [];
  const packages = Array.isArray(result.packages) ? result.packages : [];
  const errors = Array.isArray(result.templateErrors) ? result.templateErrors : [];
  const warnings = Array.isArray(result.templateWarnings) ? result.templateWarnings : [];
  const { pkgByKey, pkgByIoMatch } = buildPackageIndex(packages);

  // Index LIs by id and reverse-index IO membership.
  const liById = new Map();
  for (const li of lis) if (li.lineItemId) liById.set(li.lineItemId, li);
  // Result-level assets by assetId — line items reference them by id in
  // li.assets, which is how the launcher associates creatives too. Used to
  // surface (and round-trip) the assets' click-through URL per row.
  const assetById = new Map();
  for (const a of (Array.isArray(result.assets) ? result.assets : [])) {
    if (a && a.assetId != null) assetById.set(String(a.assetId), a);
  }
  const ioByLiId = new Map();
  for (const io of ios) {
    for (const ref of (io.lineItems || [])) if (typeof ref === 'string') ioByLiId.set(ref, io);
  }

  // Pre-build per-LI annotations from templateErrors/templateWarnings.
  // Annotation entries can name a line item by lineItemId or displayName;
  // fall back to attaching to all LIs from the named package.
  const annotationsByLiId = new Map();
  const addAnn = (liId, type, raw, msg, field = 'name') => {
    if (!liId) return;
    if (!annotationsByLiId.has(liId)) annotationsByLiId.set(liId, {});
    annotationsByLiId.get(liId)[field] = { type, raw, msg };
  };
  const resolveTargets = (ann) => {
    if (ann.lineItemId) return [ann.lineItemId];
    if (ann.displayName) {
      const hit = lis.find((l) => l.displayName === ann.displayName);
      return hit ? [hit.lineItemId] : [];
    }
    if (ann.packageKey || ann.pkid) {
      const key = ann.packageKey || ann.pkid;
      const targets = [];
      for (const io of ios) {
        const meta = parseIoDisplayName(io.displayName || '');
        if (io.packageKey === key || meta.pkId === String(key)) {
          for (const ref of (io.lineItems || [])) if (typeof ref === 'string') targets.push(ref);
        }
      }
      return targets;
    }
    return [];
  };
  for (const e of errors) {
    const targets = resolveTargets(e);
    const field = e.field || 'name';
    for (const t of targets) addAnn(t, 'm', e.raw || e.value || '—', e.message || e.msg || 'Required field missing', field);
  }
  for (const w of warnings) {
    const targets = resolveTargets(w);
    const field = w.field || 'name';
    for (const t of targets) addAnn(t, 'q', w.raw || w.value || '—', w.message || w.msg || 'Confirm value', field);
  }

  // Per-IO impressions, evenly split per LI inside the IO.
  const ioImpsCache = new WeakMap();
  const impsForIo = (io) => {
    if (ioImpsCache.has(io)) return ioImpsCache.get(io);
    const v = getIoImpressions(io, pkgByKey, pkgByIoMatch);
    ioImpsCache.set(io, v);
    return v;
  };

  const rows = [];
  let idx = 0;
  for (const [ioIdx, io] of ios.entries()) {
    const refs = Array.isArray(io.lineItems) ? io.lineItems : [];
    // An EXACT identity for "these rows belong to the same insertion order",
    // published on every row below. Not ioName: two IOs can share a display
    // name (and a copy's IOs often have no id yet), which would silently fuse
    // two orders into one group. The positional fallback is safe because the
    // key is only ever compared between rows from the same build.
    const ioGroupKey = io.insertionOrderId
      ? `ioid:${io.insertionOrderId}`
      : (io.__refPath ? `ref:${io.__refPath}` : `pos:${ioIdx}`);
    const meta = parseIoDisplayName(io.displayName || '');
    const matchedPkg = (io.packageKey && pkgByKey.get(io.packageKey))
      || (meta.pkId && pkgByIoMatch.get(`pk:${meta.pkId}`))
      || (meta.packageName && pkgByIoMatch.get(`name:${meta.packageName.toLowerCase()}`));
    const ioImps = impsForIo(io);
    const share = refs.length > 0 ? Math.round(ioImps / refs.length) : 0;
    const seg0 = io?.budget?.budgetSegments?.[0]?.dateRange || {};
    const segN = io?.budget?.budgetSegments?.[io?.budget?.budgetSegments?.length - 1]?.dateRange || {};
    const ioStart = ymdFromObj(seg0.startDate);
    const ioEnd = ymdFromObj(segN.endDate);
    const cpm = meta.rate ?? (matchedPkg ? Number(matchedPkg.rate) : null);
    // Imported plans carry raw IO micros but no package rate — derive both
    // CPM and per-LI budget from the IO totals so the grid shows real money
    // instead of '—'. Launcher plans keep their package-derived values.
    const ioBudgetDollars = ioBudgetMicros(io) / 1e6;
    const budgetCpm = cpm ?? (ioBudgetDollars > 0 && ioImps > 0
      ? Number(((ioBudgetDollars / ioImps) * 1000).toFixed(2))
      : null);
    const ioBudgetShare = ioBudgetDollars > 0 && refs.length > 0
      ? Number((ioBudgetDollars / refs.length).toFixed(2))
      : null;
    const sizes = matchedPkg?.placement_sizes?.join(', ') || '—';
    const segments = (io?.budget?.budgetSegments || []).length;
    const ioKpiAmt = microsToDollars(io.kpi?.kpiAmountMicros);
    const ioKpiType = io.kpi?.kpiType?.replace(/^KPI_TYPE_/, '') || null;

    for (const ref of refs) {
      const li = typeof ref === 'string' ? liById.get(ref) : ref;
      if (!li) continue;
      idx += 1;
      const fmt = lineItemTypeToFormat(li.lineItemType || '') || deriveFormat(li.displayName, li.tactic);
      const tactic = humanizeTactic(li.tactic);
      const bidObj = li.bidStrategy || {};
      const bidKey = Object.keys(bidObj)[0] || '';
      const bidStrategyLabel = bidKey
        ? bidKey.replace(/([A-Z])/g, ' $1').replace(/^\w/, (m) => m.toUpperCase()).trim()
        : '—';
      const bidStrategyDetail = bidObj[bidKey] || {};
      const bidMaxCpm = microsToDollars(bidStrategyDetail.maxAverageCpmBidAmountMicros || bidStrategyDetail.bidAmountMicros);
      const bidGoal = bidStrategyDetail.performanceGoalType?.replace(/^BIDDING_STRATEGY_PERFORMANCE_GOAL_TYPE_/, '') || null;

      // Audience parsing tolerates two shapes:
      //  - Canonical DV360: includedFirstPartyAndPartnerAudienceGroups is an
      //    ARRAY of groups, each with .settings[] of audience IDs.
      //  - MMGY-processed: the same key is a SINGLE OBJECT with .settings[]
      //    directly, where each entry may be:
      //       a) A keyword/interest seed: thirdpartyAudienceId === '1000'
      //          and displayName is a free-text term (e.g. "beach hotels").
      //       b) A resolved 1P/Partner audience: thirdpartyAudienceId ===
      //          'RESOLVED' with firstPartyAndPartnerAudienceId + a long
      //          taxonomic displayName (e.g. "LiveRamp Data Store > ...").
      //  Either shape collapses to a flat list of settings rows here.
      const liPlan = fillFromTargetingPlan ? planFor(result, li.lineItemId) : null;
      // geo_plan is the OTHER half of a copy's geo: names staged for resolution at
      // launch. A copy can have geo there and none in targeting_plan (a step-1
      // override on a source with no line-item geo), and if geo were left unlocked
      // in that case its ops would fall through to the normalized applier and
      // create li.geoTargeting — the union hazard this whole path exists to avoid.
      const liGeoSpec = fillFromTargetingPlan
        ? (result?.geo_plan || {})[String(li.lineItemId)] || null : null;
      const planCells = (liPlan || liGeoSpec)
        ? targetingPlanCells(liPlan, liGeoSpec) : {};
      const audRaw = li.audiences?.includedFirstPartyAndPartnerAudienceGroups;
      const audSettings = Array.isArray(audRaw)
        ? audRaw.flatMap((g) => (Array.isArray(g?.settings) ? g.settings : []))
        : (audRaw && Array.isArray(audRaw.settings) ? audRaw.settings : []);
      const keywordEntries = audSettings.filter((s) => String(s?.thirdpartyAudienceId) === '1000' && s?.displayName);
      // Audience entries: resolved 1P/partner ids, plus name-only entries
      // (displayName with no audience id) staged by the review panel's list
      // add — those are resolved by name at launch.
      const audienceEntries = audSettings.filter((s) => s && (
        String(s.thirdpartyAudienceId) === 'RESOLVED'
        || s.firstPartyAndPartnerAudienceId
        || (s.displayName && s.thirdpartyAudienceId == null)
      ));
      // Keywords come from two sources, unioned (contextual first):
      // li.contextualTargeting.includedContextualTargeting — the free-text
      // list the launcher consumes and where panel adds/edits land — plus
      // the legacy MMGY '1000' seed entries above.
      const contextualKws = Array.isArray(li.contextualTargeting?.includedContextualTargeting)
        ? li.contextualTargeting.includedContextualTargeting.filter(Boolean).map(String)
        : [];
      const keywordLabels = [...contextualKws, ...keywordEntries.map((s) => s.displayName)];
      const keywords = keywordLabels.length
        ? `${keywordLabels.length} keyword${keywordLabels.length === 1 ? '' : 's'}`
        : '—';
      const keywordList = keywordLabels.join(' · ') || '—';
      const audiences = audienceEntries.length
        ? `${audienceEntries.length} audience${audienceEntries.length === 1 ? '' : 's'} · 1P/Partner`
        : '—';
      const audienceList = audienceEntries.map((s) => s.displayName).filter(Boolean).join(' · ') || '—';
      const googleAudSettings = Array.isArray(li.audiences?.includedGoogleAudienceGroup?.settings)
        ? li.audiences.includedGoogleAudienceGroup.settings
        : [];
      const interests = googleAudSettings.length
        ? `${googleAudSettings.length} Google audience${googleAudSettings.length === 1 ? '' : 's'}`
        : '—';
      const interestIds = googleAudSettings.map((s) => s.googleAudienceId).filter(Boolean);
      const interestLabels = Object.fromEntries(googleAudSettings
        .filter((s) => s.googleAudienceId)
        .map((s) => [s.googleAudienceId, s.displayName || s.googleAudienceName
          || result._dv360DisplayNames?.audiences?.[s.googleAudienceId] || String(s.googleAudienceId)]));
      const expansion = li.targetingExpansion?.enableOptimizedTargeting
        ? 'Optimized targeting'
        : (li.targetingExpansion?.targetingExpansionLevel ? String(li.targetingExpansion.targetingExpansionLevel).replace(/^TARGETING_EXPANSION_LEVEL_/, '') : '—');

      // Geo tolerates both shapes:
      //  - DV360 canonical: includedCountries (numeric geo IDs).
      //  - MMGY-processed: includedOthers (DMA strings like
      //    "DMA: Boston (Manchester), MA"). Also include includedCities /
      //    includedRegions if present so non-MMGY plans still render
      //    something useful when no country is set.
      const geoIncludeParts = [
        ...(li.geoTargeting?.includedCountries || []).map(countryName),
        ...(li.geoTargeting?.includedOthers || []),
        ...(li.geoTargeting?.includedCities || []),
        ...(li.geoTargeting?.includedRegions || []),
      ];
      const geoExcludeParts = [
        ...(li.geoTargeting?.excludedCountries || []).map(countryName),
        ...(li.geoTargeting?.excludedOthers || []),
        ...(li.geoTargeting?.excludedCities || []),
        ...(li.geoTargeting?.excludedRegions || []),
      ];
      const geoIncludeList = geoIncludeParts.length ? geoIncludeParts.join(' · ') : '—';
      const geoExcludeList = geoExcludeParts.length ? geoExcludeParts.join(' · ') : '—';
      const geo = geoIncludeParts.length
        ? (geoExcludeParts.length ? `${geoIncludeParts.join(' · ')} · excl. ${geoExcludeParts.length}` : geoIncludeParts.join(' · '))
        : '—';

      // MMGY DV360 plans attach the matched private-marketplace deals to
      // each line item as `pmpInventorySource`: an array of
      // { inventorySourceId, displayName } (the DV360 inventory-source / PMP
      // "deal"). Surface them mirroring the TTD PMP shape so the shared review
      // grid + cell-detail panel render the deal list. Falls back to the bare
      // placement code when no PMP deals were matched (open-exchange LIs).
      const invSources = Array.isArray(li.pmpInventorySource) ? li.pmpInventorySource : [];
      const dealEntries = invSources
        .map((s) => (s && (s.displayName || s.inventorySourceId)) || '')
        .filter(Boolean);
      const pmpLabels = Object.fromEntries(invSources.filter(Boolean).map((s) => [
        s.displayName || s.inventorySourceId,
        (s.displayName && s.displayName !== String(s.inventorySourceId) ? s.displayName : null)
          || result._dv360DisplayNames?.inventory?.[s.inventorySourceId] || s.displayName || String(s.inventorySourceId),
      ]));
      const pmpDealCount = invSources.length;
      const pmpDealList = dealEntries.length
        ? dealEntries.join(' · ')
        : (li.placementId || '—');
      // Sub-label shown above the deal list in the cell-detail panel.
      const pmpInventory = pmpDealCount > 0 ? 'PMP' : null;
      // Grid cell summary: "PMP · N deals" when deals are attached, else the
      // raw placement code so the cell still says something useful.
      const pmp = pmpDealCount > 0
        ? `PMP · ${pmpDealCount} deal${pmpDealCount === 1 ? '' : 's'}`
        : (li.placementId || '—');

      const excludedDevices = li.deviceTargetingExclude?.excludedDeviceTypes || [];
      const devicesNote = excludedDevices.length ? `excl. ${excludedDevices.map((d) => d.replace(/^DEVICE_TYPE_/, '').toLowerCase()).join(', ')}` : 'all devices';

      const envs = li.environmentTargeting?.includedEnvironment || [];
      const envLabel = envs.length ? envs.map((e) => e.replace(/^ENVIRONMENT_/, '').toLowerCase().replace(/_/g, ' ')).join(', ') : '—';

      const fc = li.frequencyCap || {};
      const freqCap = fc.unlimited
        ? 'Unlimited'
        : (fc.maxImpressions != null
            ? `${fc.maxImpressions}/${fc.timeUnitCount || 1} ${(fc.timeUnit || '').replace(/^TIME_UNIT_/, '').toLowerCase()}`
            : 'No cap');

      const pacingType = li.pacing?.pacingType?.replace(/^PACING_TYPE_/, '') || 'Even';
      const pacingPeriod = li.pacing?.pacingPeriod?.replace(/^PACING_PERIOD_/, '') || 'Daily';
      const dailyCapRaw = li.pacing?.dailyMaxMicros ?? li.pacing?.dailyMaxImpressions;
      // The launcher stores impression caps under `dailyMaxMicros` when the
      // budget unit is impressions — values are too small to be currency
      // micros, so we render them as integer impression counts.
      const dailyCap = dailyCapRaw != null ? Number(dailyCapRaw) : null;

      const fc_floodlight = li.conversionCounting?.floodlightActivityConfigs?.[0];
      const pixel = fc_floodlight ? `Floodlight #${fc_floodlight.floodlightActivityId}` : 'None';
      const convWindow = fc_floodlight ? `${fc_floodlight.postClickLookbackWindowDays || 0}d click · ${fc_floodlight.postViewLookbackWindowDays || 0}d view` : '—';

      const flightInherits = (li.flight?.flightDateType || '').includes('INHERITED');
      const flightType = flightInherits ? `Inherits IO (${segments} seg)` : 'Custom';

      // Click-through URL: the distinct clickUrl(s) of the assets this LI
      // references (same spelling tolerance as the launch validator). One
      // distinct URL renders verbatim so an edit round-trips; several render
      // as a count.
      const liClickUrls = [...new Set((Array.isArray(li.assets) ? li.assets : [])
        .map((ref) => assetById.get(String(ref)))
        .map((a) => a && (a.clickUrl || a.clickURL || a.clickthroughUrl || a.clickThroughUrl || a.clickthroughURL))
        .filter(Boolean))];
      const clickURL = liClickUrls.length === 1
        ? liClickUrls[0]
        : (liClickUrls.length > 1 ? `${liClickUrls.length} URLs` : '—');

      const annotations = annotationsByLiId.get(li.lineItemId) || {};

      rows.push({
        rowId: li.lineItemId || `li-${idx}`,
        // Set when any targeting cell below was filled from the targeting_plan
        // sidecar rather than stated on the line item. CellDetailPanel turns those
        // cells READ-ONLY, and that is not cosmetic: the edit appliers only
        // understand the normalized li.geoTargeting / li.audiences shape, so an
        // edit here would CREATE that key while the sidecar entry stays — and
        // dv360_line_item_creator applies geoTargeting at create while
        // dv360_api_handler applies targeting_plan after, targeting BOTH. A remove
        // is worse: applyDv360Deletions bails on the missing key and silently does
        // nothing while the market still launches.
        __refPath: li.__refPath || null,
        idx,
        name: li.displayName || '—',
        channel: 'DV360',
        fmt,
        market: meta.packageName || io.displayName || '—',
        package: io.packageKey || '—',
        // The IO's own display name, kept alongside `market` (which prefers
        // packageName). Reconciliation keys IO-scope discrepancies on
        // io.displayName, so this is what lets them find their rows.
        ioName: io.displayName || null,
        // Groups this row with its IO siblings — see ioGroupKey above. The copy
        // grid passes it to ReviewGrid as siblingGroupField so checking one
        // line item can offer the rest of its order.
        ioGroupKey,
        category: meta.categoryLabel || '—',
        tactic,
        rawTactic: li.tactic || '—',
        start: ioStart,
        end: ioEnd,
        flightType,
        budget: (budgetCpm && share ? Number(((budgetCpm * share) / 1000).toFixed(2)) : null)
          ?? ioBudgetShare,
        cpm: budgetCpm,
        imps: share,
        dailyCap,
        // The IO's line-item count — the same divisor `share` was derived from
        // (refs.length). Published so the cell editor can disclose the IO-level
        // total an imps / budget edit will write before it is committed (see
        // DV360_SCALED_FIELDS); the cell re-renders the per-LI share, so that
        // total is otherwise invisible. Not a recomputation.
        siblingCount: refs.length || 1,
        pacing: `${pacingType} · ${pacingPeriod}`,
        geo,
        geoIncludeList,
        geoExcludeList,
        devices: devicesNote,
        environment: envLabel,
        language: '—',
        age: '—',
        gender: '—',
        audiences,
        audienceList,
        keywords,
        keywordList,
        keywordCount: keywordLabels.length,
        interests,
        interestIds,
        listDisplayLabels: {
          interests: { ...result._dv360DisplayNames?.audiences, ...interestLabels },
          pmp: { ...result._dv360DisplayNames?.inventory, ...pmpLabels },
        },
        expansion,
        pmp,
        pmpDealList,
        pmpDealCount,
        pmpInventory,
        freqCap,
        creative: (li.assets || []).length ? `${li.assets.length} asset${li.assets.length === 1 ? '' : 's'}` : '—',
        creativeFormat: sizes,
        clickURL,
        impTracker: 'DV360 native',
        bidStrategy: bidStrategyLabel,
        bidFloor: bidMaxCpm != null ? `$${bidMaxCpm.toFixed(2)}` : '—',
        bidCeiling: '—',
        viewability: li.viewability ? `${li.viewability}%` : '—',
        brandSafety: 'Standard exclusions',
        pixel,
        kpi: ioKpiType || (li.conversionCounting?.postViewCountPercentageMillis != null ? 'Conversions' : '—'),
        // Label with the IO's ACTUAL kpiType — this said "CPA" for every KPI,
        // so a $3.00 CPM goal rendered as "$3.00 CPA" one cell over from the
        // `kpi` cell correctly reading CPM. ioKpiType is already on the row.
        kpiTarget: ioKpiAmt != null ? `$${ioKpiAmt.toFixed(2)}${ioKpiType ? ` ${ioKpiType}` : ''}` : '—',
        convWindow,
        bidGoal,
        entityStatus: (li.entityStatus || '').replace(/^ENTITY_STATUS_/, ''),
        annotations,
      });

      // A COPY states none of the targeting cells above — its targeting rides the
      // targeting_plan sidecar. Fill ONLY what the template left as the empty
      // marker, so an import or spreadsheet launch is never overwritten, and mark
      // the row so CellDetailPanel locks exactly the cells that came from the
      // sidecar (see the flag's docstring for why that is a correctness matter,
      // not a polish one).
      const row = rows[rows.length - 1];
      // Gate per FIELD GROUP, not per cell. A field is filled from the sidecar only
      // when the template states NOTHING for it — otherwise an import that has geo
      // includes but no excludes would take its excludes from the sidecar and show
      // one field built from two sources.
      const PLAN_FIELD_CELLS = {
        geo: ['geo', 'geoIncludeList', 'geoExcludeList'],
        audiences: ['audiences', 'audienceList'],
        interests: ['interests', 'interestIds'],
        keywords: ['keywords', 'keywordList', 'keywordCount'],
      };
      // The panel's geo column is edited through two sub-fields.
      const PANEL_FIELDS = { geo: ['geo', 'geoInclude', 'geoExclude'] };
      const isEmptyCell = (v) => v == null || v === '—' || v === 0
        || (Array.isArray(v) && v.length === 0);
      const locked = [];
      for (const [field, cellKeys] of Object.entries(PLAN_FIELD_CELLS)) {
        const offered = cellKeys.filter((k) => k in planCells);
        if (!offered.length) continue;
        if (!cellKeys.every((k) => isEmptyCell(row[k]))) continue;
        for (const k of offered) row[k] = planCells[k];
        locked.push(...(PANEL_FIELDS[field] || [field]));
      }
      if (locked.length) {
        row.targetingFromPlan = true;
        row.targetingFromPlanFields = locked;
        // The removable entries behind each filled cell. CellDetailPanel renders
        // these and hands (field, label) back on a ×; applyDv360ListOps resolves
        // the label through this same map and prunes the plan, so what is offered
        // and what is matched cannot drift.
        const planEntries = targetingPlanEntries(liPlan, liGeoSpec);
        const kept = {};
        for (const [field, list] of Object.entries(planEntries)) {
          if (locked.includes(field)) kept[field] = list;
        }
        if (Object.keys(kept).length) {
          row.targetingEntries = { ...(row.targetingEntries || {}), ...kept };
        }
      }
    }
  }
  return rows;
}

// Campaign-level configuration card on the summary pane. Aggregates across
// all line items so the user can sanity-check the parsed plan at a glance.
export function extractDv360CampaignConfig(result) {
  if (!result) return {};
  const camp = result.campaign || {};
  const goal = camp.campaignGoal?.campaignGoalType?.replace(/^CAMPAIGN_GOAL_TYPE_/, '').replace(/_/g, ' ') || '—';
  const perfType = camp.campaignGoal?.performanceGoal?.performanceGoalType?.replace(/^PERFORMANCE_GOAL_TYPE_/, '') || '—';
  const perfAmt = microsToDollars(camp.campaignGoal?.performanceGoal?.performanceGoalAmountMicros);
  const freqCap = camp.frequencyCap?.unlimited
    ? 'Unlimited'
    : (camp.frequencyCap?.maxImpressions != null ? `${camp.frequencyCap.maxImpressions}/${camp.frequencyCap.timeUnitCount || 1} ${(camp.frequencyCap.timeUnit || '').replace(/^TIME_UNIT_/, '').toLowerCase()}` : '—');
  const lis = Array.isArray(result.lineItems) ? result.lineItems : [];
  // Average viewability target across LIs (where set).
  const viewTargets = lis.map((l) => Number(l.viewability)).filter((n) => Number.isFinite(n));
  const viewabilityTarget = viewTargets.length ? `${Math.round(viewTargets.reduce((a, b) => a + b, 0) / viewTargets.length)}%` : '—';
  // Unique floodlight pixels.
  const pixelSet = new Set();
  for (const li of lis) for (const cfg of (li.conversionCounting?.floodlightActivityConfigs || [])) {
    if (cfg.floodlightActivityId) pixelSet.add(cfg.floodlightActivityId);
  }
  // Campaign-wide exclusions, incl. template geo exclusions (MMGY's 22 countries).
  const excludeCount = Array.isArray(result.standardTargetProfiles) ? result.standardTargetProfiles.filter((p) => p.categoryDetails?.negative || p.channelDetails?.negative || p.digitalContentLabelExclusionDetails || p.operatingSystemDetails?.negative || p.geoRegionDetails?.negative).length : 0;

  // Geo summary — union of all geo targeting across LIs.
  const geoSet = new Set();
  for (const li of lis) {
    for (const id of (li.geoTargeting?.includedCountries || [])) geoSet.add(countryName(id));
    for (const o of (li.geoTargeting?.includedOthers || [])) geoSet.add(o);
    for (const c of (li.geoTargeting?.includedCities || [])) geoSet.add(c);
    for (const r of (li.geoTargeting?.includedRegions || [])) geoSet.add(r);
  }
  // A COPY states no li.geoTargeting — the source's regions ride the
  // targeting_plan sidecar instead. Fill only a genuine absence, so a
  // plan-stated geo (import / spreadsheet launch) is never overwritten.
  //
  // Exclusions are counted separately: a line item that targets everywhere EXCEPT a few
  // markets has geo, and rendering it as '—' says the opposite of what it does. The copy
  // wizard's grid already spells this 'excl. N geo targets' (copyReviewEdits.js); match it.
  let geoExcludedCount = 0;
  if (geoSet.size === 0) {
    for (const li of lis) {
      const fromPlan = targetingPlanGeo(planFor(result, li.lineItemId));
      for (const label of fromPlan.include) geoSet.add(label);
      geoExcludedCount += fromPlan.exclude.length;
    }
  }
  const geoSummary = geoSet.size === 0
    ? (geoExcludedCount
      ? `excl. ${geoExcludedCount} geo target${geoExcludedCount === 1 ? '' : 's'}`
      : '—')
    : geoSet.size <= 4 ? [...geoSet].join(', ')
    : `${geoSet.size} markets`;

  // Unique tactics across all LIs.
  const tacticSet = new Set();
  for (const li of lis) if (li.tactic) tacticSet.add(humanizeTactic(li.tactic));
  const tacticSummary = tacticSet.size ? [...tacticSet].join(', ') : '—';

  // Unique creative formats across all LIs.
  const fmtSet = new Set();
  for (const li of lis) {
    const fmt = lineItemTypeToFormat(li.lineItemType || '') || deriveFormat(li.displayName || '', li.tactic || '');
    if (fmt) fmtSet.add(fmt.toUpperCase());
  }
  const formatSummary = fmtSet.size ? [...fmtSet].join(', ') : '—';

  // Budget type from first IO.
  const ios = Array.isArray(result.insertionOrders) ? result.insertionOrders : [];
  const budgetUnit = (ios[0]?.budget?.budgetUnit || ios[0]?.budget?.budgetUnitType || '');
  const budgetType = budgetUnit.toUpperCase().includes('IMPRESSION') ? 'Impressions'
    : budgetUnit.toUpperCase().includes('CURRENCY') ? 'Currency'
    : '—';

  // Most common pacing setting across LIs.
  const pacingCounts = {};
  for (const li of lis) {
    const type = (li.pacing?.pacingType || '').replace(/^PACING_TYPE_/, '');
    const period = (li.pacing?.pacingPeriod || '').replace(/^PACING_PERIOD_/, '');
    const key = type && period ? `${type} · ${period}` : (type || period || null);
    if (key) pacingCounts[key] = (pacingCounts[key] || 0) + 1;
  }
  const topPacing = Object.entries(pacingCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || '—';

  // Most common bid strategy across LIs.
  const bidCounts = {};
  for (const li of lis) {
    const bidKey = Object.keys(li.bidStrategy || {})[0] || '';
    const label = bidKey.replace(/([A-Z])/g, ' $1').replace(/^\w/, (m) => m.toUpperCase()).trim();
    if (label) bidCounts[label] = (bidCounts[label] || 0) + 1;
  }
  const topBid = Object.entries(bidCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || '—';

  // Language targeting (union across all LIs).
  const langSet = new Set();
  for (const li of lis) {
    for (const l of (li.languageTargeting?.targetedLanguages || [])) {
      const label = String(l.displayName || l.languageId || l).replace(/^LANGUAGE_/, '');
      if (label) langSet.add(label);
    }
  }
  const languageSummary = langSet.size ? [...langSet].join(', ') : '—';

  // Unique device targets (summary of what's included / excluded).
  const deviceSet = new Set();
  for (const li of lis) {
    for (const d of (li.deviceTargeting?.targetedDeviceTypes || [])) deviceSet.add(d.replace(/^DEVICE_TYPE_/, '').toLowerCase().replace(/_/g, ' '));
  }
  const deviceSummary = deviceSet.size ? [...deviceSet].join(', ') : 'All devices';

  // Partner ID if present.
  const partnerId = result.partnerId || result.campaign?.partnerId || null;

  return {
    goal,
    kpi: perfType !== '—' ? `${perfType}${perfAmt != null ? ` · target ${fmtCurrency(perfAmt)}` : ''}` : '—',
    freqCap,
    advertiserId: result.advertiserId || null,
    viewabilityTarget,
    conversionPixels: pixelSet.size,
    standardExclusions: excludeCount,
    geoSummary,
    tacticSummary,
    formatSummary,
    budgetType,
    topPacing,
    topBid,
    languageSummary,
    deviceSummary,
    partnerId,
  };
}
